import { useState } from "react";
import { useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useUpdateProductMutation } from "@/modules/products/services/useUpdateProductMutation";
import { useDeleteProductMutation } from "@/modules/products/services/useDeleteProductMutation";
import { useCreateProductUploadIntentMutation } from "@/modules/products/services/useCreateProductUploadIntentMutation";
import { useUpdateProductImagesMutation } from "@/modules/products/services/useUpdateProductImagesMutation";
import { useUploadToSignedUrlMutation } from "@/shared/services/useUploadToSignedUrlMutation";
import { validateImageFile } from "@/modules/products/components/product-images/helpers";

/** Smallest shape the actions need — every view can supply this. */
export type ProductActionTarget = {
  id: string;
  name: string;
  isActive: boolean;
};

export type ProductCardActions = {
  /** Flip visibility inline (no navigation). Toasts on success/error. */
  toggleVisibility: (product: ProductActionTarget) => Promise<void>;
  /** Id of the product whose visibility flip is in flight, or null. */
  pendingVisibilityId: string | null;
  /** Open the delete confirmation for a product. */
  requestDelete: (product: ProductActionTarget) => void;
  /** Currently-targeted delete (drives the ConfirmDialog), or null. */
  deleteTarget: ProductActionTarget | null;
  cancelDelete: () => void;
  confirmDelete: () => Promise<void>;
  isDeleting: boolean;
  /** Open the inline stock editor for a product (by id). */
  requestEditStock: (productId: string) => void;
  /** Product id whose stock editor is open (drives the sheet), or null. */
  stockTargetId: string | null;
  closeEditStock: () => void;
  /** Upload a first photo for a product without leaving the list. */
  addPhoto: (productId: string, file: File) => Promise<void>;
  /** Product id whose photo upload is in flight, or null. */
  pendingPhotoId: string | null;
};

/**
 * Centralizes the interactive product actions (inline visibility toggle +
 * delete) so the grid card, desktop table and mobile list share one
 * implementation — same optimistic cache patch, same toasts, same confirm
 * flow. The owning page renders a single <ConfirmDialog> driven by
 * `deleteTarget`.
 */
export function useProductCardActions({
  storeSlug,
}: {
  storeSlug: string;
}): ProductCardActions {
  const t = useTranslations();
  const toast = useToast();
  const { mutateAsync: updateProduct } = useUpdateProductMutation();
  const { mutateAsync: deleteProduct, isPending: isDeleting } =
    useDeleteProductMutation();
  const { mutateAsync: createUploadIntent } =
    useCreateProductUploadIntentMutation();
  const { mutateAsync: uploadToSignedUrl } = useUploadToSignedUrlMutation();
  const { mutateAsync: updateImages } = useUpdateProductImagesMutation();
  const [pendingPhotoId, setPendingPhotoId] = useState<string | null>(null);

  const [pendingVisibilityId, setPendingVisibilityId] = useState<string | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<ProductActionTarget | null>(
    null,
  );
  const [stockTargetId, setStockTargetId] = useState<string | null>(null);

  // Silent revert used by the Undo affordance — flips back to the prior state
  // and re-confirms with a plain toast (no second Undo, so we can't loop).
  const revertVisibility = async (productId: string, restoreActive: boolean) => {
    setPendingVisibilityId(productId);
    try {
      await updateProduct({ storeSlug, productId, isActive: restoreActive });
      toast.success(
        restoreActive
          ? t(keys.products.toastShown)
          : t(keys.products.toastHidden),
      );
    } catch {
      // Error is surfaced globally via the React Query MutationCache handler.
    } finally {
      setPendingVisibilityId(null);
    }
  };

  const toggleVisibility = async (product: ProductActionTarget) => {
    if (pendingVisibilityId) return;
    const next = !product.isActive;
    setPendingVisibilityId(product.id);
    try {
      await updateProduct({
        storeSlug,
        productId: product.id,
        isActive: next,
      });
      // Reversible action → no confirmation, but offer a 6s Undo so an accidental
      // flip is a recoverable non-event.
      toast.success(
        next ? t(keys.products.toastShown) : t(keys.products.toastHidden),
        undefined,
        {
          durationMs: 6000,
          action: {
            label: t(keys.common.undo),
            onClick: () => {
              void revertVisibility(product.id, product.isActive);
            },
          },
        },
      );
    } catch {
      // Error is surfaced globally via the React Query MutationCache handler.
    } finally {
      setPendingVisibilityId(null);
    }
  };

  // Light "add first photo" straight from the list: validate → upload intent →
  // PUT to the signed URL → PATCH the product's images. Heavy editing (crop,
  // reorder, bg-removal) still lives on the edit page.
  const addPhoto = async (productId: string, file: File) => {
    if (pendingPhotoId) return;
    setPendingPhotoId(productId);
    try {
      await validateImageFile(file);
      const intent = await createUploadIntent({
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
        productId,
      });
      await uploadToSignedUrl({
        uploadUrl: intent.uploadUrl,
        file,
        contentType: file.type,
      });
      await updateImages({
        storeSlug,
        productId,
        images: [
          { imageId: intent.imageId, objectKey: intent.objectKey, sortOrder: 0 },
        ],
      });
      toast.success(t(keys.products.toastPhotoAdded));
    } catch {
      // Error is surfaced globally via the React Query MutationCache handler.
    } finally {
      setPendingPhotoId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteProduct({ storeSlug, productId: deleteTarget.id });
      toast.success(t(keys.products.toastDeleted));
      setDeleteTarget(null);
    } catch {
      // Error is surfaced globally via the React Query MutationCache handler.
    }
  };

  return {
    toggleVisibility,
    pendingVisibilityId,
    requestDelete: setDeleteTarget,
    deleteTarget,
    cancelDelete: () => setDeleteTarget(null),
    confirmDelete,
    isDeleting,
    requestEditStock: setStockTargetId,
    stockTargetId,
    closeEditStock: () => setStockTargetId(null),
    addPhoto,
    pendingPhotoId,
  };
}
